import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const OscarImporter = () => {
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState("production");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Selecciona un archivo primero");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data_type", dataType);
      const res = await axios.post(
        `${API}/api/analytics/import-oscar-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        }
      );
      setResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message || "Import OK");
      } else {
        toast.error(res.data.error || "Error en import");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo fichero");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid="oscar-importer-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-primary" />
          <CardTitle>Importar fichero Oscar</CardTitle>
        </div>
        <CardDescription>
          Sube el Excel/CSV que te envíe Redforts con datos de Producción o Facturación.
          La app detectará columnas (año, mes, cantidad, estancias) y actualizará la BD respetando meses bloqueados.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium" htmlFor="file-input">Archivo (.xlsx, .xls o .csv)</label>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              onChange={(e) => setFile(e.target.files[0])}
              className="block mt-1 w-full text-sm border rounded p-2"
              data-testid="oscar-file-input"
            />
            {file && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="datatype-select">Tipo de datos</label>
            <Select value={dataType} onValueChange={setDataType}>
              <SelectTrigger id="datatype-select" data-testid="data-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Producción (monthly_stats)</SelectItem>
                <SelectItem value="billing">Facturación (monthly_billing)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full"
          data-testid="upload-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </>
          )}
        </Button>

        {result && (
          <Card className={result.success ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className={`w-5 h-5 mt-0.5 ${result.success ? "text-emerald-600" : "text-red-600"}`} />
                <div className="flex-1">
                  <p className="font-bold">{result.message || result.error}</p>
                  {result.success && (
                    <div className="flex gap-2 mt-2">
                      <Badge className="bg-emerald-600">{result.inserted} insertados</Badge>
                      <Badge className="bg-blue-600">{result.updated} actualizados</Badge>
                      {result.skipped_locked > 0 && (
                        <Badge className="bg-amber-600">{result.skipped_locked} bloqueados</Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
          <p className="font-semibold mb-1">💡 Formato esperado del fichero:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-800">
            <li>Cabeceras: <code>año</code>, <code>mes</code>, <code>cantidad</code>, <code>estancias</code></li>
            <li>Acepta .xlsx, .xls (Excel antiguo), .csv (separadores , o ;), HTML/XML</li>
            <li>Formatos numéricos €1.234,56 o 1234.56</li>
            <li>Meses como número (1-12), o nombre (Ene, Enero, Jan)</li>
            <li>Meses bloqueados (locked) NO se sobreescriben</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default OscarImporter;
